import React, { useEffect, useState } from 'react';

import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import MobileMenu from 'components/MobileMenu';
import RenderMenuContent from 'components/RenderMenuContent';
import { Button } from 'components/ui/button';
import { Separator } from 'components/ui/separator';
import { Sheet, SheetContent, SheetTitle } from 'components/ui/sheet';
import { useMenu } from 'global/MenuContext';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useDynamicLogo } from 'hooks/useDynamicLogo';
import { useViewportHeight } from 'hooks/useViewportHeight';
import { cn } from 'lib/utils';
import { useMeetingRooms } from 'pages/Rooms/hooks/useMeetingRooms';
import { useTheme } from 'providers/ThemeProvider';
import { MeetingRoomExtras, MenuItem, isMeetingRoomExtras } from 'types/menuTypes';
import { logger } from 'utils/logger';

let backgroundImageDark: string | undefined;
let backgroundImageLight: string | undefined;
try {
  backgroundImageDark = require('assets/images/header/header-background.png');
} catch {
  backgroundImageDark = undefined;
}
try {
  backgroundImageLight = require('assets/images/header/header-background-light.png');
} catch {
  backgroundImageLight = undefined;
}

interface LayoutMobileProps {
  children: React.ReactNode;
}

export const LayoutMobile = ({ children }: LayoutMobileProps) => {
  const { t } = useTranslation('common');
  const { isDark } = useTheme();
  const { currentLevel, firstLevelMenu, secondLevelMenu, thirdLevelMenu, selectMenuByDocumentId } =
    useMenuSelection();
  const { menu } = useMenu();
  const { logo, isLoading: logoLoading } = useDynamicLogo();
  const { collections } = useMeetingRooms();
  const { isSafari, isMobile } = useViewportHeight();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [headerTitle, setHeaderTitle] = useState('');
  const location = useLocation();
  const { roomId } = useParams();
  const lastSegment = location.pathname.split('/').filter(Boolean).pop();
  const roomIdentifier = roomId || lastSegment;

  const findActiveItem = (items: MenuItem[], path: string): MenuItem | undefined => {
    for (const item of items) {
      if (item.path === path) {
        return item;
      }
      if (item.subMenu) {
        const found = findActiveItem(item.subMenu, path);
        if (found) return found;
      }
    }
    return undefined;
  };

  const currentItem = findActiveItem(menu, location.pathname);
  const isMeetingRoom = currentItem?.feature?.dynamic_component === 'MEETING_ROOM';
  const isMeetingRoomRoom = currentItem?.feature?.dynamic_component === 'MEETING_ROOM_ROOM';

  const currentRoom =
    (isMeetingRoom || isMeetingRoomRoom) && collections
      ? collections
          .flatMap(collection => collection.rooms)
          .find(
            room =>
              room.id?.toString() === roomIdentifier ||
              room.room_data?.id?.toString() === roomIdentifier ||
              room.internal_id?.toString() === roomIdentifier
          )
      : null;

  const meetingBackground =
    currentRoom?.background?.url ||
    (currentItem?.feature && isMeetingRoomExtras(currentItem?.feature?.extras ?? null)
      ? (currentItem.feature.extras as MeetingRoomExtras).background?.url
      : undefined);

  const getHeaderTitle = () => {
    if ((isMeetingRoom || isMeetingRoomRoom) && currentRoom && secondLevelMenu) {
      return secondLevelMenu.label || '';
    }

    if (currentLevel === 'third' && thirdLevelMenu && secondLevelMenu) {
      return secondLevelMenu.label || '';
    }

    if (currentLevel === 'second' && secondLevelMenu) {
      return secondLevelMenu.label || '';
    }

    if (currentLevel === 'first' && firstLevelMenu) {
      return firstLevelMenu.label || '';
    }

    return '';
  };

  useEffect(() => {
    const newTitle = getHeaderTitle();
    setHeaderTitle(newTitle);
  }, [currentLevel, firstLevelMenu, secondLevelMenu, thirdLevelMenu, currentRoom]);

  const handleThirdLevelMenuClick = (selectedItem: MenuItem) => {
    if (selectedItem.feature?.documentId) {
      selectMenuByDocumentId(selectedItem.feature.documentId, true, undefined, selectedItem.path);
    } else if (selectedItem.extras && isMeetingRoomExtras(selectedItem.extras ?? null)) {
      selectMenuByDocumentId(
        (selectedItem.extras as MeetingRoomExtras).internal_id || '',
        true,
        undefined,
        selectedItem.path
      );
    }
    setDrawerOpen(false);
  };

  const getThirdLevelItems = () => {
    if (currentLevel === 'third' && secondLevelMenu?.subMenu) {
      return secondLevelMenu.subMenu;
    }
    return [];
  };

  const thirdLevelItems = getThirdLevelItems();

  const renderContent = () => {
    switch (currentLevel) {
      case 'first':
        logger.debug('[LayoutMobile] Rendering first level:', firstLevelMenu?.label);
        return <RenderMenuContent menu={firstLevelMenu}>{children}</RenderMenuContent>;
      case 'second':
        logger.debug('[LayoutMobile] Rendering second level:', secondLevelMenu?.label);
        return <RenderMenuContent menu={secondLevelMenu}>{children}</RenderMenuContent>;
      case 'third':
        logger.debug('[LayoutMobile] Rendering third level:', thirdLevelMenu?.label);
        return <RenderMenuContent menu={thirdLevelMenu}>{children}</RenderMenuContent>;
      default:
        logger.debug('[LayoutMobile] Rendering default children');
        return children;
    }
  };

  const headerBg = isDark ? backgroundImageDark : backgroundImageLight;

  return (
    <>
      {/* AppBar replacement */}
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50',
          'h-14 flex items-center justify-center',
          'bg-card shadow-sm'
        )}
        style={{
          backgroundImage: headerBg ? `url(${headerBg})` : undefined,
          backgroundSize: headerBg ? 'cover' : undefined,
          backgroundPosition: headerBg ? 'center' : undefined,
          backgroundRepeat: headerBg ? 'no-repeat' : undefined,
        }}
      >
        {/* Toolbar replacement */}
        <div className="w-full px-4 h-14 flex items-center justify-between">
          <div className="flex items-center">
            {!logoLoading && logo && <img src={logo} alt="Logo" className="max-h-[4.5rem]" />}
          </div>
          <h6 className="ml-4 flex-1 text-right text-foreground text-xl font-bold">
            {headerTitle}
          </h6>
        </div>
      </header>

      {/* Main content area */}
      <div
        className={cn(
          'mobile-viewport-height',
          'bg-background',
          meetingBackground ? 'mt-14' : 'mt-16'
        )}
        style={{
          backgroundImage: meetingBackground ? `url(${meetingBackground})` : undefined,
          backgroundSize: meetingBackground ? 'cover' : undefined,
          backgroundPosition: meetingBackground ? 'center' : undefined,
          backgroundRepeat: meetingBackground ? 'no-repeat' : undefined,
        }}
      >
        {/* Third level menu button */}
        {thirdLevelItems.length > 1 && (
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={() => setDrawerOpen(true)}
              className={cn(
                'w-[95%] my-2 h-12',
                'justify-between',
                'font-normal text-base',
                'border-[1.5px] border-border',
                'shadow-none'
              )}
            >
              <span>{currentItem?.label || thirdLevelItems[0].label}</span>
              <ChevronDown className="w-7 h-7 text-muted-foreground" />
            </Button>

            {/* Bottom Sheet for third level menu */}
            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
              <SheetContent
                side="bottom"
                className={cn('rounded-t-2xl', 'min-h-[30vh] max-h-[auto]', 'bg-background')}
              >
                <SheetTitle className="sr-only">{t('navigation.menu')}</SheetTitle>
                <div className="p-2">
                  <Separator className="mb-2" />
                  <div className="flex flex-col gap-2">
                    {thirdLevelItems.map(item => (
                      <button
                        key={
                          item.feature?.documentId ||
                          (isMeetingRoomExtras(item.extras ?? null)
                            ? (item.extras as any).internal_id
                            : undefined) ||
                          item.path
                        }
                        disabled={item.isActive === false}
                        onClick={() => handleThirdLevelMenuClick(item)}
                        className={cn(
                          'w-full text-left px-4 py-3 rounded-lg',
                          'transition-colors',
                          item.path === currentItem?.path
                            ? 'bg-accent text-accent-foreground'
                            : 'hover:bg-accent/50',
                          item.isActive === false && 'opacity-50 cursor-not-allowed'
                        )}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        )}

        <div className="mobile-content-area mx-2">{renderContent()}</div>
      </div>

      <MobileMenu />
    </>
  );
};

export default LayoutMobile;
